// booking/hooks/useBookingActions.ts
import { useCallback, useMemo } from 'react'
import type { BookingAction, BookingState } from '../state/booking.reducer'
import { Cart } from '../types/booking'

import posthog from 'posthog-js'
import { apiCreateCart } from '../services/bookingApi'

import { trackAddToCart, trackBeginCheckout, trackCustomerData } from '../services/bookingTracking'
import { breadcrumb, setBookingTags, setBookingContext } from '../services/sentryBooking'
import { addCheckoutData, addAddonsToCart, BookingItem } from '../services/api'
import { handleAppError } from '../lib/errors/handleAppError'
import { isNoAvailabilityError, SessionUnavailableError } from '../lib/errors/availability'
import { nextStageOf, stageNumberOf } from '../lib/bookingConfig'
import { groupBy } from 'lodash'
import { CustomerFormFields } from '../stages/CustomerDetails/types'
import { customerFormFromCart } from '../stages/CustomerDetails/lib/customerDetails'
import { useBookingExternal } from '../state/external.context'
import { useTranslations } from 'next-intl'

export function useBookingActions(opts: {
  state: BookingState
  dispatch: React.Dispatch<BookingAction>
  trackHistory: (stage: number) => void
}) {
  const { state, dispatch, trackHistory } = opts
  const { locale } = useBookingExternal()
  const t = useTranslations('booking')

  const onCompleteExtrasSelection = useCallback(async () => {
    const next = nextStageOf(state.config, 'extras')

    breadcrumb('extras.complete', { extras: state.extras }, 'booking.flow')
    setBookingTags({ stage: next })
    posthog.capture('extras_selection_completed', {
      extras_count: state.extras.reduce((sum, e) => sum + e.quantity, 0),
    })
    trackHistory(next)
  }, [state.extras, state.config, trackHistory])

  const onCompleteAddonsSelection = useCallback(async () => {
    const cart = state.cart
    if (!cart) return

    const next = nextStageOf(state.config, 'addons')

    breadcrumb('addons.complete', { addons: state.selectedAddons }, 'booking.flow')

    try {
      await addAddonsToCart(cart.id, state.selectedAddons)
      setBookingTags({ stage: next })
      posthog.capture('addons_selection_completed', {
        selected_addons: state.selectedAddons,
        cart_id: cart.id,
      })
      trackHistory(next)
    } catch (e) {
      handleAppError(e, { dispatch, t })
    }
  }, [state.cart, state.selectedAddons, state.config, dispatch, trackHistory, t])

  const onCompleteQuantitySelection = useCallback(async () => {
    const values = state.quantities
    const next = nextStageOf(state.config, 'quantity')

    breadcrumb('quantities.complete', { counts: values }, 'booking.flow')
    setBookingContext({ quantities: values })

    trackAddToCart(values)
    setBookingTags({ stage: next })
    posthog.capture('ticket_quantity_selected', {
      ticket_types: values.map((q) => q.category),
      total_tickets: values.reduce((sum, q) => sum + q.quantity, 0),
      quantities: values.map((q) => ({ category: q.category, quantity: q.quantity })),
    })
    trackHistory(next)
  }, [state.quantities, state.config, trackHistory])

  const onCompleteDateTime = useCallback(async () => {
    const session = state.session
    if (!session) return

    const quantities = state.quantities
    const next = nextStageOf(state.config, 'datetime')

    breadcrumb('session.select', { session_id: session.id }, 'booking.flow')
    setBookingContext({ session_id: session.id, session_date_time: session.dateTime })

    try {
      const activeExtras = state.extras.filter((e) => e.quantity > 0)

      const cart = await apiCreateCart({
        locale: locale,
        sessionId: session.id,
        bookingItems: quantities.map(({ category, quantity }) => ({
          category: category,
          quantity: quantity,
        })),
        ...(activeExtras.length > 0 && {
          extraItems: activeExtras.map(({ category, quantity }) => ({ category, quantity })),
        }),
      })

      trackBeginCheckout(quantities, session)
      setBookingTags({ stage: next })
      posthog.capture('checkout_started', {
        session_id: session.id,
        session_datetime: session.dateTime,
        cart_id: cart?.id ?? null,
        total_tickets: quantities.reduce((sum, q) => sum + q.quantity, 0),
      })
      trackHistory(next)

      dispatch({ type: 'SET_CART', cart: cart })
      dispatch({ type: 'SET_PAYMENT_READY', paymentReady: true })
    } catch (e) {
      // The session filled up between listing it and creating the cart. Not a
      // fault - hand it to the stage as a typed error so it can de-select the
      // session and mark it sold out, and skip the generic error toast.
      if (isNoAvailabilityError(e)) {
        breadcrumb('session.unavailable', { session_id: session.id }, 'booking.flow')
        posthog.capture('session_unavailable', {
          session_id: session.id,
          session_datetime: session.dateTime,
          total_tickets: quantities.reduce((sum, q) => sum + q.quantity, 0),
        })
        throw new SessionUnavailableError(session.id)
      }

      handleAppError(e, { dispatch, t })
      // Re-throw so ContinueButton knows the submit failed and re-enables itself.
      throw e
    }
  }, [dispatch, state.quantities, state.extras, state.session, state.config, trackHistory, locale, t])

  const onCompleteCustomerData = useCallback(
    async (data: CustomerFormFields) => {
      const cart = state.cart
      if (!cart) return

      // Only capture field keys, not values (PII safety)
      const fieldKeys = data ? Object.keys(data) : []
      breadcrumb('customer_details.complete', { fieldKeys }, 'booking.flow')

      const refreshedCart = await addCheckoutData(cart.id, data)

      trackCustomerData(data) // GA4 already; keep Sentry clean

      if (data.email) {
        posthog.identify(data.email, {
          email: data.email,
          first_name: data.firstName ?? null,
          last_name: data.lastName ?? null,
          country: data.country ?? null,
        })
      }

      posthog.capture('customer_details_submitted', {
        cart_id: cart.id,
        country: data.country ?? null,
        has_phone: Boolean(data.phone),
      })

      dispatch({ type: 'SET_CART', cart: refreshedCart })
      dispatch({ type: 'SET_CUSTOMER_DATA', customerData: data })

      if (cart.total === 0) {
        const thankyouStage = stageNumberOf(state.config, 'thankyou')
        setBookingTags({ stage: thankyouStage })
        trackHistory(thankyouStage)
      } else {
        const paymentStage = stageNumberOf(state.config, 'payment')
        setBookingTags({ stage: paymentStage })
        trackHistory(paymentStage)
      }
    },
    [dispatch, trackHistory, state.cart, state.config],
  )

  const cartLinesToBookingItems = (lines: Cart['lines']): BookingItem[] => {
    const linesByCategory = groupBy(
      lines.filter((line) => line.category && !line.voucher /*&& !line.physical*/),
      'category',
    )
    return Object.entries(linesByCategory).map(([id, group]) => ({
      category: id,
      quantity: group.length,
    }))
  }

  const rebuildCart = useCallback(
    async (cart?: Cart) => {
      const useCart = cart ? cart : state.cart
      if (!useCart) return null
      console.log('Rebuilding cart...')
      // Back from a payment gateway the page has reloaded and the form is empty,
      // but the cart still has the customer's details.
      const customerData =
        state.customerData.email || !useCart.customer
          ? state.customerData
          : customerFormFromCart(useCart.customer)
      const activeExtras = state.extras.filter((e) => e.quantity > 0)
      const res = await apiCreateCart({
        locale: useCart.locale,
        sessionId: useCart.session.id,
        bookingItems: cartLinesToBookingItems(useCart.lines),
        ...(activeExtras.length > 0 && {
          extraItems: activeExtras.map(({ category, quantity }) => ({ category, quantity })),
        }),
      })
      let finalRes = null
      if (res) finalRes = await addCheckoutData(res.id, customerData)
      if (customerData !== state.customerData) dispatch({ type: 'SET_CUSTOMER_DATA', customerData })
      dispatch({ type: 'SET_CART', cart: finalRes })
      return finalRes
    },
    [state.cart, state.extras, state.customerData, dispatch],
  )

  return useMemo(
    () => ({
      onCompleteExtrasSelection,
      onCompleteAddonsSelection,
      onCompleteQuantitySelection,
      onCompleteDateTime,
      onCompleteCustomerData,
      rebuildCart,
      //completeDateTime,
      //completeCustomerDetails,
      //rebuildCart,
    }),
    [
      onCompleteExtrasSelection,
      onCompleteAddonsSelection,
      onCompleteQuantitySelection,
      onCompleteDateTime,
      onCompleteCustomerData,
      rebuildCart /*completeDateTime, completeCustomerDetails, rebuildCart*/,
    ],
  )
}
